import { Injectable } from "@angular/core";
import {
  Dataset,
  EventType,
  Job,
  Resource,
  Rule,
  Session,
  SessionEvent,
  Label,
  SessionState,
  WsEvent,
} from "chipster-js-common";
import log from "loglevel";
import { EMPTY, Observable, Subject, defer, of as observableOf } from "rxjs";
import { catchError, filter, map, mergeMap, share } from "rxjs/operators";
import { WebSocketSubject } from "rxjs/webSocket";
import { ErrorService } from "../../../core/errorhandler/error.service";
import { SessionData } from "../../../model/session/session-data";
import { SessionResource } from "../../../shared/resources/session.resource";
import { WebSocketService } from "../../../shared/services/websocket.service";
import { SelectionService } from "./selection.service";
import { FileState } from "chipster-js-common/lib/model/dataset";

@Injectable()
export class SessionEventService {
  sessionId: string;

  datasetStream$: Observable<SessionEvent>;
  jobStream$: Observable<SessionEvent>;
  sessionStream$: Observable<SessionEvent>;
  ruleStream$: Observable<SessionEvent>;
  labelStream$: Observable<SessionEvent>;
  wsSubject$: WebSocketSubject<WsEvent>;
  localSubject$: Subject<WsEvent>;

  sessionHasChanged = false;

  /**
   * When each handleOrDrop() message was last shown to the user, so that an ongoing
   * failure doesn't stack a new toast for every event. These toasts don't time out.
   */
  private lastReported = new Map<string, number>();

  /** Show the same handleOrDrop() message again only after this many milliseconds */
  private static readonly ERROR_INTERVAL_MS = 30 * 1000;

  constructor(
    private sessionResource: SessionResource,
    private websocketService: WebSocketService,
    private errorService: ErrorService,
    private selectionService: SelectionService,
  ) {}

  unsubscribe() {
    this.websocketService.unsubscribe();
  }

  setSessionData(sessionId: string, sessionData: SessionData) {
    this.sessionHasChanged = false;
    this.lastReported.clear();
    this.sessionId = sessionId;

    this.localSubject$ = new Subject();
    const stream = this.localSubject$.asObservable();

    this.websocketService.connect(this.localSubject$, "sessions/" + sessionId);

    // track any changes to session
    stream.subscribe({
      next: () => {
        this.sessionHasChanged = true;
      },
      error: (err) => this.errorService.showError("session change tracking failed", err),
    });

    this.datasetStream$ = stream.pipe(
      filter((wsData) => wsData.resourceType === Resource.Dataset),
      mergeMap((data) =>
        this.handleOrDrop("dataset event error", () =>
          this.handleDatasetEvent(data, this.sessionId, sessionData).pipe(
            // update type tags before letting other parts of the client know about this change
            mergeMap((sessionEvent) => this.updateTypeTags(this.sessionId, sessionEvent, sessionData)),
          ),
        ),
      ),
      share(),
    );

    this.jobStream$ = stream.pipe(
      filter((wsData) => wsData.resourceType === Resource.Job),
      mergeMap((data) =>
        this.handleOrDrop("job event error", () => this.handleJobEvent(data, this.sessionId, sessionData)),
      ),
      share(),
    );

    this.sessionStream$ = stream.pipe(
      filter((wsData) => wsData.resourceType === Resource.Session),
      mergeMap((data) =>
        this.handleOrDrop("session event error", () => this.handleSessionEvent(data, this.sessionId, sessionData)),
      ),
      share(),
    );

    this.ruleStream$ = stream.pipe(
      filter((wsData) => wsData.resourceType === Resource.Rule),
      mergeMap((data) => this.handleOrDrop("rule event error", () => this.handleRuleEvent(data, sessionData.session))),
      share(),
    );

    this.labelStream$ = stream.pipe(
      filter((wsData) => wsData.resourceType === Resource.Label),
      mergeMap((data) =>
        this.handleOrDrop("label event error", () => this.handleLabelEvent(data, this.sessionId, sessionData)),
      ),
      share(),
    );

    // update sessionData even if no one else subscribes
    // handleOrDrop() reports the failures of individual events, but report also if the
    // stream itself errors, because then it stops updating sessionData altogether
    this.datasetStream$.subscribe({
      error: (err) => this.errorService.showError("dataset event stream failed", err),
    });
    this.jobStream$.subscribe({
      error: (err) => this.errorService.showError("job event stream failed", err),
    });
    this.sessionStream$.subscribe({
      error: (err) => this.errorService.showError("session event stream failed", err),
    });
    this.ruleStream$.subscribe({
      error: (err) => this.errorService.showError("rule event stream failed", err),
    });
    this.labelStream$.subscribe({
      error: (err) => this.errorService.showError("label event stream failed", err),
    });
  }

  /**
   * Handle one event, and report and drop it if the handling fails, so that
   * the stream stays alive for the events that follow. Without this, one
   * failed request or one handler that throws would end the stream for
   * everyone subscribed to it. The handler runs inside defer(), so a throw
   * before it returns an observable is caught too.
   *
   * The same message is shown at most once in ERROR_INTERVAL_MS, because the stream stays
   * alive and a persistent failure (an expired token, session-db down) would otherwise
   * show a new error for every event.
   */
  private handleOrDrop<T>(message: string, handle: () => Observable<T>): Observable<T> {
    return defer(handle).pipe(
      catchError((err) => {
        const now = Date.now();
        const last = this.lastReported.get(message);
        if (last != null && now - last < SessionEventService.ERROR_INTERVAL_MS) {
          // the error is probably still on the screen, showing it again for every event
          // would fill the screen with toasts, because these don't time out
          log.warn(message + " (repeated, not shown to the user)", err);
        } else {
          this.lastReported.set(message, now);
          this.errorService.showError(message, err);
        }
        return EMPTY;
      }),
    );
  }

  /**
   * Update sessionData and create a stream of old and new values. The old values
   * are useful for detecting changes.
   *
   * @returns {Observable<SessionEvent>}
   */
  getDatasetStream() {
    return this.datasetStream$;
  }

  getJobStream() {
    return this.jobStream$;
  }

  getRuleStream() {
    return this.ruleStream$;
  }

  getLabelStream() {
    return this.labelStream$;
  }

  getSelectedDatasetsContentsUpdatedStream(): Observable<string> {
    return this.getDatasetStream().pipe(
      filter(
        (sessionEvent) =>
          sessionEvent != null &&
          sessionEvent.event.type === EventType.Update &&
          sessionEvent.event.resourceType === Resource.Dataset &&
          this.selectionService.selectedDatasets.some(
            (selectedDataset) => selectedDataset.datasetId === (sessionEvent.newValue as Dataset).datasetId,
          ),
      ),
      map((sessionEvent) => (sessionEvent.newValue as Dataset).datasetId),
    );
  }

  createEvent(event, oldValue, newValue) {
    return observableOf(new SessionEvent(event, oldValue, newValue));
  }

  handleRuleEvent(event: any, session: Session): Observable<SessionEvent> {
    log.info("handle rule event", event, session);
    if (event.type === EventType.Create) {
      return this.sessionResource.getRule(session.sessionId, event.resourceId).pipe(
        mergeMap((rule: Rule) => {
          session.rules.push(rule);
          return this.createEvent(event, null, rule);
        }),
      );
    }
    if (event.type === EventType.Delete) {
      const rule = session.rules.find((r) => r.ruleId === event.resourceId);
      session.rules = session.rules.filter((r) => r.ruleId !== event.resourceId);
      return this.createEvent(event, rule, null);
    }
    log.warn("unknown event type", event);
    return EMPTY;
  }

  handleSessionEvent(event: any, sessionId: any, sessionData: SessionData): Observable<SessionEvent> {
    if (event.type === EventType.Update) {
      if (event.state !== SessionState.Delete) {
        return this.sessionResource.getSession(sessionId).pipe(
          mergeMap((remote: Session) => {
            const local = sessionData.session;
            sessionData.session = remote;
            log.info("session updated", remote.name, remote.state);
            return this.createEvent(event, local, remote);
          }),
        );
      }
      // nothing to do, the client reacts when the Rule is deleted
      return EMPTY;
    }
    if (event.type === EventType.Delete) {
      // nothing to do, the client reacts when the Rule is deleted
      return EMPTY;
    }
    log.warn("unknown event type", event);
    return EMPTY;
  }

  handleDatasetEvent(event: any, sessionId: string, sessionData: SessionData): Observable<SessionEvent> {
    if (event.type === EventType.Create) {
      // accept only complete datasets, see SessionResource.loadSession()
      if (event.state !== FileState.Complete) {
        log.info("wait until upload is completed", event);
        return EMPTY;
      }

      return this.sessionResource.getDataset(sessionId, event.resourceId).pipe(
        mergeMap((remote: Dataset) => {
          // datasetsMap.set() is deferred to updateTypeTags() so that the dataset
          // is never visible in the UI before its type tags are ready
          return this.createEvent(event, null, remote);
        }),
      );
    }
    if (event.type === EventType.Update) {
      // accept only complete datasets, see SessionResource.loadSession()
      if (event.state !== FileState.Complete) {
        log.info("wait until upload is completed", event);
        return EMPTY;
      }

      return this.sessionResource.getDataset(sessionId, event.resourceId).pipe(
        mergeMap((remote: Dataset) => {
          const local = sessionData.datasetsMap.get(event.resourceId);
          // datasetsMap.set() is deferred to updateTypeTags() so that the dataset
          // is never visible in the UI before its type tags are ready
          return this.createEvent(event, local, remote);
        }),
      );
    }
    if (event.type === EventType.Delete) {
      const localCopy = sessionData.datasetsMap.get(event.resourceId);
      sessionData.datasetsMap.delete(event.resourceId);
      return this.createEvent(event, localCopy, null);
    }
    log.warn("unknown event type", event);
    return EMPTY;
  }

  updateTypeTags(sessionId, sessionEvent, sessionData) {
    // update type tags before
    const newValue = <Dataset>sessionEvent.newValue;

    if (newValue) {
      if (!newValue.fileId) {
        // dataset created, but fileId is missing
        // get type tags later when the fileId is added
        return observableOf(sessionEvent);
      }

      // dataset created or updated, update type tags and datasetsMap together
      // so the dataset is never visible in the UI before its type tags are ready
      return this.sessionResource.getTypeTagsForDataset(sessionId, newValue).pipe(
        map((typeTags) => {
          sessionData.datasetTypeTags.set(newValue.datasetId, typeTags);
          sessionData.datasetsMap.set(newValue.datasetId, newValue);
          return sessionEvent;
        }),
        catchError((err) => {
          if (err?.status === 404) {
            // dataset was deleted before type tags were fetched, nothing to do
            log.info("dataset deleted before type tags were fetched", newValue.datasetId);
            return observableOf(sessionEvent);
          }
          throw err;
        }),
      );
    }
    // dataset deleted, type tags can be removed
    sessionData.datasetTypeTags.delete(sessionEvent.event.resourceId);
    return observableOf(sessionEvent);
  }

  handleLabelEvent(event: any, sessionId: string, sessionData: SessionData): Observable<SessionEvent> {
    if (event.type === EventType.Create || event.type === EventType.Update) {
      return this.sessionResource.getLabel(sessionId, event.resourceId).pipe(
        mergeMap((remote: Label) => {
          const local = sessionData.labelsMap.get(event.resourceId);
          sessionData.labelsMap.set(event.resourceId, remote);
          return this.createEvent(event, local, remote);
        }),
      );
    }
    if (event.type === EventType.Delete) {
      const localCopy = sessionData.labelsMap.get(event.resourceId);
      sessionData.labelsMap.delete(event.resourceId);
      return this.createEvent(event, localCopy, null);
    }
    log.warn("unknown event type", event);
    return EMPTY;
  }

  handleJobEvent(event: any, sessionId: any, sessionData: SessionData): Observable<SessionEvent> {
    if (event.type === EventType.Create) {
      return this.sessionResource.getJob(sessionId, event.resourceId).pipe(
        mergeMap((remote: Job) => {
          sessionData.jobsMap.set(event.resourceId, remote);
          return this.createEvent(event, null, remote);
        }),
      );
    }
    if (event.type === EventType.Update) {
      return this.sessionResource.getJob(sessionId, event.resourceId).pipe(
        mergeMap((remote: Job) => {
          const local = sessionData.jobsMap.get(event.resourceId);
          sessionData.jobsMap.set(event.resourceId, remote);
          return this.createEvent(event, local, remote);
        }),
      );
    }
    if (event.type === EventType.Delete) {
      const localCopy = sessionData.jobsMap.get(event.resourceId);
      sessionData.jobsMap.delete(event.resourceId);
      return this.createEvent(event, localCopy, null);
    }
    log.warn("unknown event type", event.type, event);
    return EMPTY;
  }

  /**
   * Handle a locally generated event just like the real events coming from the websocket.
   *
   * Through this the client can be tricked to show different state from the server. Obviously
   * should be used only for quick hacks.
   *
   * @param event
   */
  generateLocalEvent(event: WsEvent) {
    if (event.sessionId !== this.sessionId) {
      /*
      The user has opened another session after this event was created. The event handling
      above applies events to the currently open session without checking the event's
      sessionId, so events of other sessions would corrupt it.
      */
      log.info("skip local event of another session", event.sessionId);
      return;
    }
    // incorrect typing? it really is an object, but the compiler wants a string
    this.localSubject$.next(event);
  }
}
